import { PushNotifications, Token, ActionPerformed } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { isNativePlatform } from '../utils/platform';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Type for listener handle
interface PluginListenerHandle {
  remove: () => Promise<void>;
}

export interface NotificationPayload {
  title: string;
  body: string;
  data?: Record<string, string>;
}

/**
 * Initialize push notifications for the current platform
 * On native: Uses FCM via @capacitor/push-notifications
 * On web: Could integrate web push (future enhancement)
 */
export const initializePushNotifications = async (userId: string): Promise<void> => {
  if (!isNativePlatform()) {
    console.log('Web platform - native push notifications not available');
    return;
  }

  try {
    // Request permission
    const permissionResult = await PushNotifications.requestPermissions();

    if (permissionResult.receive === 'granted') {
      // Register with FCM
      await PushNotifications.register();

      console.log('Push notifications registered successfully');

      // Get FCM token using the official Firebase Messaging plugin
      try {
        console.log('Getting FCM token from Firebase Messaging plugin...');
        const result = await FirebaseMessaging.getToken();
        const token = result.token;
        console.log('FCM Token:', token);

        // Save token to Firestore
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          fcmTokens: arrayUnion(token),
          lastTokenUpdate: new Date().toISOString(),
        });
        console.log('FCM token saved to Firestore');
      } catch (tokenError) {
        console.error('Error getting/saving FCM token:', tokenError);
        console.error('Error details:', JSON.stringify(tokenError));
      }
    } else {
      console.warn('Push notification permission denied');
    }
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    throw error;
  }
};

/**
 * Set up push notification listeners
 * Call this once during app initialization after user is authenticated
 */
export const setupPushNotificationListeners = (
  userId: string,
  onNotificationReceived?: (notification: NotificationPayload) => void,
  onNotificationTapped?: (notification: NotificationPayload) => void
): void => {
  if (!isNativePlatform()) {
    return;
  }

  // Called when a new FCM token is generated
  PushNotifications.addListener('registration', async (token: Token) => {
    console.log('FCM Token:', token.value);

    // Save token to Firestore user document
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token.value),
        lastTokenUpdate: new Date().toISOString(),
      });
      console.log('FCM token saved to Firestore');
    } catch (error) {
      console.error('Error saving FCM token:', error);
    }
  });

  // Called when registration fails
  PushNotifications.addListener('registrationError', (error: any) => {
    console.error('Push notification registration error:', error);
  });

  // Called when notification is received while app is in foreground
  PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
    console.log('Push notification received (foreground):', notification);

    const payload: NotificationPayload = {
      title: notification.title || '',
      body: notification.body || '',
      data: notification.data || {},
    };

    if (onNotificationReceived) {
      onNotificationReceived(payload);
    }
  });

  // Called when user taps on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.log('Push notification action performed:', action);

    const notification = action.notification;
    const payload: NotificationPayload = {
      title: notification.title || '',
      body: notification.body || '',
      data: notification.data || {},
    };

    if (onNotificationTapped) {
      onNotificationTapped(payload);
    }
  });
};

/**
 * Remove FCM token when user signs out
 */
export const removePushNotificationToken = async (_userId: string): Promise<void> => {
  if (!isNativePlatform()) {
    return;
  }

  try {
    // Remove listeners
    PushNotifications.removeAllListeners();

    console.log('Push notification listeners removed');
  } catch (error) {
    console.error('Error removing push notification token:', error);
  }
};

/**
 * Get the current FCM token (if available)
 */
export const getCurrentPushToken = async (): Promise<string | null> => {
  if (!isNativePlatform()) {
    return null;
  }

  return new Promise((resolve) => {
    let listenerHandle: PluginListenerHandle | null = null;

    // Set up temporary listener to get token
    PushNotifications.addListener('registration', (token: Token) => {
      listenerHandle?.remove();
      resolve(token.value);
    }).then((handle) => {
      listenerHandle = handle;
    });

    // Trigger registration
    PushNotifications.register().catch(() => {
      listenerHandle?.remove();
      resolve(null);
    });

    // Timeout after 5 seconds
    setTimeout(() => {
      listenerHandle?.remove();
      resolve(null);
    }, 5000);
  });
};

/**
 * Handle notification navigation based on data payload
 * This should be called when user taps a notification
 */
export const handleNotificationNavigation = (data: Record<string, string>, navigate: (path: string) => void): void => {
  const { type } = data;

  switch (type) {
    case 'booking_confirmed':
    case 'meeting_reminder':
    case 'reschedule_request':
    case 'reschedule_approved':
    case 'reschedule_declined':
    case 'meeting_cancelled':
      // Navigate to meetings tab
      navigate('/app?tab=meetings');
      break;

    case 'new_review':
      // Navigate to reviews/ratings
      navigate('/app?tab=profile');
      break;

    case 'payout_processed':
    case 'payment_received':
      // Navigate to billing
      navigate('/app?tab=profile');
      break;

    default:
      // Navigate to home
      navigate('/app');
      break;
  }
};
